/**
 * Calculate effective total count based on pagination mode and provided totals
 *
 * @returns Effective total count (-1 if unknown)
 */
export function calculateTotalCount({
  mode,
  entitiesLength,
  totalCount,
  hasMoreItems
}) {
  if (mode === "client") {
    return entitiesLength;
  }
  // If total count is known, use it
  if (totalCount !== null && totalCount !== undefined) {
    return totalCount;
  }
  // If we have unknown total but no more items to load, we know the exact total
  if (hasMoreItems === false) {
    return entitiesLength;
  }
  // Unknown total with more items available
  return -1;
}
/**
 * Calculate the last page index (0-based)
 * Returns -1 if the last page cannot be determined
 *
 * @returns The last page index (0-based) or -1 if unknown
 */
export function calculateLastPage({
  effectiveTotalCount,
  pageSize,
  entitiesLength,
  hasMoreItems
}) {
  if (effectiveTotalCount !== -1) {
    return Math.ceil(effectiveTotalCount / pageSize) - 1;
  }
  if (!hasMoreItems && entitiesLength > 0) {
    return Math.ceil(entitiesLength / pageSize) - 1;
  }
  return -1;
}
/**
 * Calculate display indices for the footer (1-based for display)
 *
 * @returns Object with 'from' and 'to' indices
 */
export function calculateDisplayIndices({
  pageIndex,
  pageSize,
  paginatedLength
}) {
  const start = pageIndex * pageSize;
  return {
    from: paginatedLength > 0 ? start + 1 : 0,
    to: start + paginatedLength
  };
}
/**
 * Determine the direction of page change
 *
 * @returns "next" if moving to the next page, "previous" if moving to the previous page, "jump" otherwise
 */
export function getPageChangeDirection({
  currentPage,
  newPage
}) {
  if (newPage === currentPage + 1) {
    return "next";
  }
  if (newPage === currentPage - 1) {
    return "previous";
  }
  return "jump";
}
/**
 * Check if the next page button should be disabled
 *
 * @returns True if the next page button should be disabled, false otherwise
 */
export function shouldDisableNextButton({
  pageIndex,
  lastPage,
  entitiesLength,
  pageSize,
  hasMoreItems
}) {
  if (lastPage !== -1) {
    return pageIndex >= lastPage;
  }
  // For unknown totals, disable if no more items
  return !hasMoreItems && pageIndex >= Math.floor(entitiesLength / pageSize);
}
/**
 * Load next page of data if needed when navigating forward.
 *
 * @returns A promise that resolves when the fetch completes or immediately if no fetch is needed.
 * **Important**: If `fetchNextPage` throws an error, it will propagate through the returned promise.
 * Callers should handle errors appropriately (e.g., await with try-catch, or use .catch()).
 *
 * @example
 * ```ts
 * // Caller should handle errors
 * loadNextPage({...params})
 *   .catch(error => {
 *     console.error('Failed to load next page:', error);
 *     // Show error toast, etc.
 *   });
 * ```
 */
export function loadNextPage({
  newPage,
  pageSize,
  entitiesLength,
  setIsLoadingNextPage,
  totalCount,
  fetchNextPage
}) {
  const requiredEnd = Math.min((newPage + 1) * pageSize, totalCount ?? Infinity);
  const isFetchRequired = entitiesLength < requiredEnd;
  if (isFetchRequired && fetchNextPage) {
    setIsLoadingNextPage(true);
    return fetchNextPage().finally(() => {
      setIsLoadingNextPage(false);
    });
  }
  return Promise.resolve();
}